import { MouseEventHandler, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Divider } from '@mui/material';
import { AnimatePresence } from 'framer-motion';

import AuthStore from '@/stores/AuthStore';
import { CommentType } from '@/types/Content/Comment';
import CustomAvatar from '@/components/CustomAvatar';
import { useCommentStore } from '../CommentContext';
import {
  ActionsSection,
  CommentBody,
  CommentContainer,
  CommentDate,
  CommenterName,
  DeleteButton,
  Header,
  OptionsWrapper,
} from './Comment.styles';
import { CommentListWrapper } from '../CommentList/CommentList.styles';
import LeaveCommentBlock from '../LeaveCommentBlock/LeaveCommentBlock';
import CommentForm from '../CommentForm/CommentForm';

export interface CommentProps {
  comment: CommentType;
  className?: string;
  showReply?: boolean;
  grayInput?: boolean;
  lastItem?: boolean;
  parentId?: string;
}

function Comment({
  comment,
  className,
  showReply = true,
  grayInput,
  lastItem = false,
  parentId,
}: CommentProps): JSX.Element {
  const commentStore = useCommentStore();
  const { isLoading } = commentStore;
  const { user, createdAt, body, replies } = comment;
  const [repliesVis, setRepliesVis] = useState(false);

  const repliesReversed = useMemo(
    () => (repliesVis ? replies?.slice().reverse() : []),
    [replies, repliesVis],
  );

  if (!user) {
    return null;
  }

  const { cmsUser } = AuthStore;
  const userName = user?.firstName;
  const commenterName = `${user?.firstName} ${user?.lastName}`;
  const isCommentAuthor = user?.id === cmsUser?.id;
  const userImage = user?.image?.url;

  const onClickDelete: MouseEventHandler = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    commentStore.pushToDeletionQueue(
      parentId ? { ...comment, parentId } : comment,
    );
  };
  const toggleRepliesVis = () => setRepliesVis((prev) => !prev);

  return (
    <CommentContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={className}
      id={comment.id}
    >
      <Header>
        <CustomAvatar
          name={userName}
          image={userImage}
          size={{ desktop: 11.5, mobile: 10 }}
          fontSize={{ desktop: 6, mobile: 5 }}
        />
        <div style={{ flex: 1 }}>
          <CommenterName>{commenterName}</CommenterName>&#12539;
          <CommentDate publishedAt={createdAt} />
        </div>
        <OptionsWrapper>
          {isCommentAuthor && (
            <DeleteButton onClick={onClickDelete} disabled={isLoading} />
          )}
        </OptionsWrapper>
      </Header>
      <CommentBody variant="body1">{body}</CommentBody>
      {showReply && (
        <ActionsSection>
          <LeaveCommentBlock
            replies
            collapsed={!repliesVis}
            handleToggle={toggleRepliesVis}
            commentsSize={replies?.length || 0}
          />
        </ActionsSection>
      )}

      {repliesVis ? (
        <>
          <Divider variant="fullWidth" sx={{ marginTop: '16px' }} />
          <CommentListWrapper replies>
            <div>
              <CommentForm
                onCancelClick={toggleRepliesVis}
                parentId={comment.id}
                grayInput={grayInput}
              />
              {repliesReversed?.length ? <Divider variant="fullWidth" /> : null}
            </div>
            <AnimatePresence>
              {repliesReversed?.map((item) => (
                <Comment
                  key={item.id}
                  comment={item}
                  className={className}
                  showReply={false}
                  parentId={comment.id}
                />
              ))}
            </AnimatePresence>
          </CommentListWrapper>
          {!lastItem && <Divider variant="fullWidth" />}
        </>
      ) : null}
    </CommentContainer>
  );
}

Comment.defaultProps = {
  className: '',
};

export default observer(Comment);
